import { Box, Paper, Typography } from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useMemo } from "react";
import { useFragment } from "react-relay";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { InternBrandActivityChart_brand$key } from "./__generated__/InternBrandActivityChart_brand.graphql";
import { useColorMode } from "../../../contexts/ColorModeContext";
import formatNumber from "../../../utils/formatNumber";

const brandFragment = graphql`
  fragment InternBrandActivityChart_brand on BrandType {
    metricsDaily(filters: { os: { isNull: true } }, first: 30) {
      edges {
        node {
          date
          appSessions
          appSales
          newAppUsers
        }
      }
    }
  }
`;

type Props = {
  brand: InternBrandActivityChart_brand$key;
};

const InternBrandActivityChart = ({ brand: brandKey }: Props) => {
  const brand = useFragment(brandFragment, brandKey);
  const { colorMode } = useColorMode();

  // Prepare trend chart data
  const trendData = useMemo(() => {
    const edges = brand.metricsDaily?.edges ?? [];
    // Reverse to show oldest first (chronological order)
    return [...edges]
      .reverse()
      .map((edge) => {
        if (!edge?.node) return null;
        return {
          date: new Date(edge.node.date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          sessions: edge.node.appSessions ?? 0,
          newUsers: edge.node.newAppUsers ?? 0,
        };
      })
      .filter(Boolean);
  }, [brand.metricsDaily]);

  // Theme-aware colors
  const chartColors = {
    grid: colorMode === "dark" ? "#374151" : "#e5e7eb",
    text: colorMode === "dark" ? "#9ca3af" : "#6b7280",
    tooltip: {
      bg: colorMode === "dark" ? "#1f2937" : "#ffffff",
      border: colorMode === "dark" ? "#374151" : "#e5e7eb",
    },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        bgcolor: "var(--intern-bg-primary)",
        border: "1px solid var(--intern-border-default)",
        borderRadius: 2,
        height: "100%",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          color: "var(--intern-text-secondary)",
          fontWeight: 600,
          mb: 2,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          fontSize: "0.6875rem",
        }}
      >
        Activity (30 Days)
      </Typography>
      <Box sx={{ height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={trendData}
            margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorNewUsers" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={chartColors.grid}
              vertical={false}
            />
            <XAxis
              dataKey="date"
              stroke={chartColors.text}
              fontSize={10}
              tickLine={false}
              axisLine={false}
              tick={{ fill: chartColors.text }}
              interval="preserveStartEnd"
            />
            <YAxis
              stroke={chartColors.text}
              fontSize={10}
              tickLine={false}
              axisLine={false}
              tick={{ fill: chartColors.text }}
              tickFormatter={(value) => formatNumber(value)}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: chartColors.tooltip.bg,
                border: `1px solid ${chartColors.tooltip.border}`,
                borderRadius: 8,
                fontSize: "0.75rem",
              }}
              formatter={(value: number, name: string) => [
                formatNumber(value),
                name === "sessions" ? "Sessions" : "New Users",
              ]}
              labelStyle={{ color: chartColors.text }}
            />
            <Legend
              iconType="circle"
              iconSize={8}
              formatter={(value) => (
                <span
                  style={{
                    color: chartColors.text,
                    fontSize: "0.75rem",
                  }}
                >
                  {value === "sessions" ? "Sessions" : "New Users"}
                </span>
              )}
            />
            <Area
              type="monotone"
              dataKey="sessions"
              stroke="#6366f1"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorSessions)"
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey="newUsers"
              stroke="#10b981"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorNewUsers)"
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

export default InternBrandActivityChart;
